import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  TrendingUp, 
  TrendingDown,
  BarChart3, 
  FolderKanban, 
  FileText,
  Euro,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const BudgetComWidget = ({ className }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchBudgetData();
  }, [selectedYear]);

  const fetchBudgetData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/wallet/budget-com', { 
        params: { year: selectedYear } 
      });
      setData(res.data);
    } catch (error) {
      console.error('Error fetching budget data:', error);
      // Fallback to demo data
      setData(generateDemoData());
    } finally {
      setLoading(false);
    }
  };

  const generateDemoData = () => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const currentMonth = new Date().getMonth();
    
    const monthlyData = months.map((month, idx) => {
      // Only show data up to current month
      if (idx > currentMonth && selectedYear === new Date().getFullYear()) {
        return { month, projets: 0, contrats: 0, total: 0 };
      }
      
      const projets = Math.floor(Math.random() * 3000) + 500;
      const contrats = Math.floor(Math.random() * 800) + 200;
      return {
        month,
        projets,
        contrats,
        total: projets + contrats
      };
    });

    const totalProjets = monthlyData.reduce((sum, m) => sum + m.projets, 0);
    const totalContrats = monthlyData.reduce((sum, m) => sum + m.contrats, 0);

    return {
      year: selectedYear,
      monthly_data: monthlyData,
      total_projets: totalProjets,
      total_contrats: totalContrats,
      total_budget: totalProjets + totalContrats,
      vs_last_year: Math.floor(Math.random() * 40) - 10 // -10% to +30%
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-800 mb-2">{label} {selectedYear}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-slate-600">{entry.name}:</span>
              <span className="font-medium text-slate-800">{formatCurrency(entry.value)}</span>
            </div>
          ))}
          <div className="border-t border-slate-200 mt-2 pt-2 flex items-center gap-2 text-sm">
            <Euro className="w-3 h-3 text-slate-500" />
            <span className="font-semibold text-slate-800">Total: {formatCurrency(total)}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className={cn("", className)}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data?.monthly_data || [];
  const vsLastYear = data?.vs_last_year || 0;

  return (
    <Card className={cn("border-0 shadow-sm", className)} data-testid="budget-com-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-slate-800">
                Budget Communication
              </CardTitle>
              <p className="text-xs text-slate-500">
                Vos dépenses annuelles
              </p>
            </div>
          </div>
          
          {/* Year selector */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setSelectedYear(y => y - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-2 font-medium text-sm text-slate-700 min-w-[50px] text-center">
              {selectedYear}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setSelectedYear(y => y + 1)}
              disabled={selectedYear >= new Date().getFullYear()}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-2">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <FolderKanban className="w-4 h-4 text-indigo-600" />
              <span className="text-xs text-indigo-600 font-medium">Projets</span>
            </div>
            <p className="font-bold text-lg text-indigo-900">
              {formatCurrency(data?.total_projets || 0)}
            </p>
          </div>
          
          <div className="p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-emerald-600 font-medium">Contrats</span>
            </div>
            <p className="font-bold text-lg text-emerald-900">
              {formatCurrency(data?.total_contrats || 0)}
            </p>
          </div>
          
          <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <Euro className="w-4 h-4 text-purple-600" />
              <span className="text-xs text-purple-600 font-medium">Total {selectedYear}</span>
            </div>
            <p className="font-bold text-lg text-purple-900">
              {formatCurrency(data?.total_budget || 0)}
            </p>
            {vsLastYear !== 0 && (
              <div className={cn(
                "flex items-center gap-1 text-xs mt-1",
                vsLastYear > 0 ? "text-emerald-600" : "text-red-600"
              )}>
                {vsLastYear > 0 ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                <span>{vsLastYear > 0 ? '+' : ''}{vsLastYear}% vs {selectedYear - 1}</span>
              </div>
            )}
          </div>
        </div>

        {/* Chart */}
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorProjets" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorContrats" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top"
                height={30}
                iconType="circle"
                iconSize={8}
                formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
              />
              <Area 
                type="monotone" 
                dataKey="projets" 
                name="Projets"
                stroke="#6366f1" 
                fillOpacity={1} 
                fill="url(#colorProjets)" 
                strokeWidth={2}
              />
              <Area 
                type="monotone" 
                dataKey="contrats" 
                name="Contrats mensuels"
                stroke="#10b981" 
                fillOpacity={1} 
                fill="url(#colorContrats)" 
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend explanation */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
              <span>Projets ponctuels</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span>Abonnements & maintenance</span>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px]">
            <Calendar className="w-3 h-3 mr-1" />
            Année {selectedYear}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export default BudgetComWidget;
